import { Chip as NovaBaseChip, ChipProps as NovaChipProps } from '@fme-nova/components'
import { ChipProps as MuiChipProps, default as MuiBaseChip } from '@mui/material/Chip'
import React from 'react'

import { COLORS, createComponentWrapperWithRef, styled } from '../../theme'
import { MsdkStyledComponent } from '../../utils'

const MuiChip: MsdkStyledComponent<MuiChipProps> = styled(MuiBaseChip)`
  &.MuiChip-filled {
    background-color: ${COLORS.action.selected};
    color: ${COLORS.text.primary};

    &:hover {
      background-color: #0000001f;
    }

    &:disabled {
      opacity: 0.38;
    }

    & .MuiChip-deleteIcon {
      color: ${COLORS.action.active};
    }

    & .MuiChip-avatar {
      background-color: #7b7b7b;
      color: #fff;
    }

    &.MuiChip-filledPrimary {
      background-color: ${COLORS.primary.main};
      color: ${COLORS.primary.contrast};

      &:hover {
        background-color: ${COLORS.primary.dark};
      }

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.primary.dark};
      }
    }

    &.MuiChip-filledError {
      background-color: ${COLORS.error.main};
      color: ${COLORS.primary.contrast};

      &:hover {
        background-color: ${COLORS.error.dark};
      }

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.error.dark};
      }
    }

    &.MuiChip-filledWarning {
      background-color: ${COLORS.warning.main};
      color: ${COLORS.primary.contrast};

      &:hover {
        background-color: ${COLORS.warning.dark};
      }

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.warning.dark};
      }
    }

    &.MuiChip-filledInfo {
      background-color: ${COLORS.info.main};
      color: ${COLORS.primary.contrast};

      &:hover {
        background-color: ${COLORS.info.dark};
      }

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.info.dark};
      }
    }

    &.MuiChip-filledSuccess {
      background-color: ${COLORS.success.main};
      color: ${COLORS.primary.contrast};

      &:hover {
        background-color: ${COLORS.success.dark};
      }

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.success.dark};
      }
    }
  }

  &.MuiChip-outlined {
    border: 1px solid #c7c7c7;
    color: ${COLORS.text.primary};

    &:hover {
      background-color: #f7f7f7;
    }

    &:disabled {
      opacity: 0.38;
    }

    & .MuiChip-deleteIcon {
      color: ${COLORS.action.active};
    }

    & .MuiChip-avatar {
      background-color: #7b7b7b;
      color: #fff;
    }

    &.MuiChip-outlinedPrimary {
      border: 1px solid ${COLORS.primary.main};
      color: ${COLORS.primary.main};

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.primary.dark};
      }
    }

    &.MuiChip-outlinedError {
      border: 1px solid ${COLORS.error.main};
      color: ${COLORS.error.main};

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.error.dark};
      }
    }

    &.MuiChip-outlinedWarning {
      border: 1px solid ${COLORS.warning.main};
      color: ${COLORS.warning.main};

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.warning.dark};
      }
    }

    &.MuiChip-outlinedInfo {
      border: 1px solid ${COLORS.info.main};
      color: ${COLORS.info.main};

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }

      & .MuiChip-avatar {
        background-color: ${COLORS.info.dark};
      }
    }

    &.MuiChip-outlinedSuccess {
      border: 1px solid ${COLORS.success.main};
      color: ${COLORS.success.main};

      & .MuiChip-deleteIcon {
        color: #0000008a;
      }
    }
  }
`

const NovaChip: MsdkStyledComponent<NovaChipProps> = styled(NovaBaseChip)``

type CommonChipColor = 'info' | 'success' | 'warning' | 'error'
type MuiChipColor = 'default' | 'primary' | 'secondary' | CommonChipColor
type NovaChipColor = 'neutral' | 'off' | CommonChipColor

export type ChipProps = Omit<MuiChipProps, 'color'> & {
  color?: MuiChipColor | NovaChipColor
  className?: string
}

const RefHolder = styled('div')`
  overflow: hidden;
  display: inline-flex;
`

const MuiChipWrapper = React.forwardRef<HTMLDivElement, ChipProps>(function MuiChipWrapper({ color, ...props }, ref) {
  return (
    <RefHolder ref={ref}>
      <MuiChip color={color as MuiChipColor} {...props} />
    </RefHolder>
  )
})

const NovaChipWrapper = React.forwardRef<HTMLDivElement, ChipProps>(function NovaChipWrapper(
  { color, className, ...props },
  ref,
) {
  return (
    <RefHolder ref={ref} className={className}>
      <NovaChip color={color as NovaChipColor} {...props} />
    </RefHolder>
  )
})

export const Chip = createComponentWrapperWithRef(
  {
    mui: MuiChipWrapper,
    nova: NovaChipWrapper,
    propsEnhancer(props, componentLibrary) {
      if (componentLibrary === 'nova') {
        if (props.color === 'default') {
          props.color = 'neutral'
        }
        if (props.color === 'primary') {
          props.color = 'info'
        }
        if (props.color === 'secondary') {
          props.color = 'neutral'
        }
      }

      if (componentLibrary === 'mui') {
        if (props.color === 'neutral') {
          props.color = 'default'
        }
        if (props.color === 'off') {
          props.color = 'default'
        }
      }

      return props
    },
  },
  'Chip',
)
